import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Disc3, Loader2, Upload } from "lucide-react";
import Layout from "@/components/Layout";
import { LoadingPanel } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { brandVars, loadMeta } from "@/lib/brand";
import { cn } from "@/lib/utils";

const inputCls = "w-full h-10 rounded-lg border border-line bg-card px-3 text-[13.5px] text-ink outline-none focus:border-brand focus:ring-2 focus:ring-brand/20";
const COLORS = [
  ["#43295F", "MetaTrack purple"],
  ["#1F3A5F", "Navy"],
  ["#1B5E4A", "Forest"],
  ["#7A2E3A", "Wine"],
  ["#2B2B2B", "Charcoal"],
  ["#B5651D", "Copper"],
];
const MODES = [
  ["standard", "Standard", "The colour panel with your headline, as it is today."],
  ["promo", "Promo image", "Your image behind the headline, darkened so the words stay readable."],
  ["photo", "Image only", "Just your image (a finished artwork or flyer). The headline is hidden."],
];
const DEFAULTS = {
  platform_name: "MetaTrack",
  tagline: "Every song, split, code and registration — in one place.",
  blurb: "Your catalog, the writers on it and where each work is registered. The app spots missing codes and splits that don't add up for you.",
  brand_color: "#43295F",
  login_mode: "standard",
  cta_on: false,
  cta_text: "",
  cta_url: "",
  promo_until: "",
};

function readFile(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

function Section({ title, children }) {
  return (
    <div className="px-5 py-4 border-t border-line first:border-t-0 space-y-3">
      <div className="font-mono text-[10.5px] font-semibold tracking-[0.12em] uppercase text-muted2">{title}</div>
      {children}
    </div>
  );
}

function ImageUpload({ label, hint, thumb, wide, onPick, busy }) {
  const ref = useRef(null);
  return (
    <div className="flex gap-3 items-center rounded-xl border-[1.5px] border-dashed border-line px-3 py-2.5">
      <div className={cn("shrink-0 rounded-xl overflow-hidden bg-surface border border-line grid place-items-center", wide ? "w-24 h-14" : "w-14 h-14")}>{thumb}</div>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-semibold">{label}</div>
        <div className="text-[11.5px] text-muted2 leading-snug">{hint}</div>
      </div>
      <input
        ref={ref}
        type="file"
        accept="image/*"
        className="sr-only"
        onChange={async (e) => {
          const f = e.target.files?.[0];
          e.target.value = "";
          if (!f) return;
          if (f.size > 15 * 1024 * 1024) return toast.error("That image is too big — keep it under 15 MB");
          onPick(await readFile(f));
        }}
      />
      <Button type="button" variant="outline" size="sm" onClick={() => ref.current?.click()} disabled={busy}>
        <Upload className="w-4 h-4 mr-1.5" /> Upload
      </Button>
    </div>
  );
}

/** The icon as it will look: uploaded/picked image, or the standard disc on the chosen colour. */
function IconPreview({ src, color, size, radius }) {
  if (src) return <img src={src} alt="" style={{ width: size, height: size, borderRadius: radius }} className="object-cover shrink-0" />;
  return (
    <span className="grid place-items-center shrink-0" style={{ width: size, height: size, borderRadius: radius, background: `linear-gradient(180deg, ${color}, ${color}cc)` }}>
      <Disc3 className="text-white" style={{ width: size * 0.55, height: size * 0.55 }} />
    </span>
  );
}

function Preview({ f, icon, promo }) {
  const [tab, setTab] = useState("desk");
  const mode = f.login_mode;
  const promoOn = mode !== "standard" && promo;
  const cta = f.cta_on && f.cta_text ? (
    <span className="inline-flex items-center gap-1 mt-2.5 rounded-full bg-white text-[#1C1A21] font-bold text-[10.5px] px-3 py-1">
      {f.cta_text} <ArrowRight className="w-3 h-3" />
    </span>
  ) : null;
  const panelBg = (
    <>
      {promoOn ? <img src={promo} alt="" className="absolute inset-0 w-full h-full object-cover" /> : null}
      {promoOn && mode === "promo" && <span className="absolute inset-0 bg-gradient-to-b from-black/25 to-black/60" />}
    </>
  );
  const form = (
    <div className="w-full max-w-[200px] flex flex-col gap-2 text-[#1C1A21]">
      <div className="font-display text-[17px] font-semibold">Sign in</div>
      <span className="text-[#655F6C] -mt-1.5 text-[11px]">Pick up where you left off.</span>
      <span className="font-semibold text-[9.5px] -mb-1">Email</span>
      <span className="h-7 rounded-md border border-[#E6E1D8] bg-white" />
      <span className="font-semibold text-[9.5px] -mb-1">Password</span>
      <span className="h-7 rounded-md border border-[#E6E1D8] bg-white" />
      <span className="h-[30px] rounded-md grid place-items-center text-white font-bold text-[10.5px]" style={{ background: f.brand_color }}>
        Sign in
      </span>
    </div>
  );
  let body;
  let note;
  if (tab === "desk") {
    body = (
      <div className="w-full max-w-[680px] aspect-[16/10] rounded-xl overflow-hidden grid grid-cols-[1.05fr_1fr] bg-[#F5F3EE] shadow-xl text-[11px]">
        <div className="relative text-white p-5 flex flex-col justify-between overflow-hidden" style={{ background: f.brand_color }}>
          {panelBg}
          <div className="relative flex items-center gap-2 font-display font-semibold text-[16px]">
            <IconPreview src={icon} color={f.brand_color} size={26} radius={7} />
            {f.platform_name || "MetaTrack"}
          </div>
          <div className="relative">
            {(!promoOn || mode === "promo") && (
              <>
                <div className="font-display text-[19px] leading-[1.15] font-semibold max-w-[270px]">{f.tagline}</div>
                <div className="opacity-80 mt-1.5 max-w-[270px] leading-snug">{f.blurb}</div>
              </>
            )}
            {cta}
          </div>
          <span />
        </div>
        <div className="flex items-center justify-center p-4">{form}</div>
      </div>
    );
    note = mode === "standard" ? "The sign-in page in your colour." : promo ? "The promo shows beside the sign-in form. Writers see it too when they sign in." : "Upload a promo image to see it here.";
  } else if (tab === "phone") {
    body = (
      <div className="w-[250px] h-[520px] rounded-[36px] bg-[#111] p-[9px] shadow-xl">
        <div className="w-full h-full rounded-[28px] overflow-hidden bg-[#F5F3EE] text-[#1C1A21] flex flex-col relative text-[11px]">
          <span className="absolute left-1/2 -translate-x-1/2 top-[7px] w-[76px] h-[22px] rounded-full bg-[#111] z-10" />
          {promoOn ? (
            <div className="relative h-[180px] shrink-0 text-white p-4 pt-10 flex flex-col justify-end overflow-hidden" style={{ background: f.brand_color }}>
              {panelBg}
              <div className="relative">
                {mode === "promo" && <div className="font-display text-[15px] leading-tight font-semibold">{f.tagline}</div>}
                {cta}
              </div>
            </div>
          ) : (
            <div className="pt-10" />
          )}
          <div className="p-4 flex-1">
            <div className="flex items-center gap-2 font-display font-semibold text-[14px] mb-3">
              <IconPreview src={icon} color={f.brand_color} size={24} radius={7} />
              {f.platform_name || "MetaTrack"}
            </div>
            {form}
          </div>
        </div>
      </div>
    );
    note = promoOn ? "On phones the promo becomes a banner above the sign-in form." : "On phones the colour panel is hidden, so the form comes first.";
  } else {
    body = (
      <div className="flex flex-col items-center gap-4 w-full">
        <div className="w-[250px] h-[400px] rounded-[36px] bg-[#111] p-[9px] shadow-xl">
          <div className="w-full h-full rounded-[28px] grid grid-cols-4 gap-x-1.5 gap-y-4 content-start px-4 pt-12" style={{ background: "linear-gradient(160deg,#3B2A55,#1A2340 60%,#13283A)" }}>
            {Array.from({ length: 6 }).map((_, i) => (
              <span key={i} className="flex flex-col items-center gap-1 text-[9px] text-white">
                <span className="w-11 h-11 rounded-[11px] bg-white/20" />
                App
              </span>
            ))}
            <span className="flex flex-col items-center gap-1 text-[9px] text-white text-center">
              <IconPreview src={icon} color={f.brand_color} size={44} radius={11} />
              {(f.platform_name || "MetaTrack").slice(0, 12)}
            </span>
          </div>
        </div>
        <div className="bg-[#DEE1E6] rounded-lg pt-2 px-2.5 w-full max-w-[420px]">
          <div className="bg-[#F1F3F4] rounded-t-md h-[30px] w-[220px] flex items-center gap-2 px-3 text-[12px] text-[#1C1A21]">
            <IconPreview src={icon} color={f.brand_color} size={16} radius={4} />
            {f.platform_name || "MetaTrack"} — Catalog
          </div>
        </div>
      </div>
    );
    note = "Home screen, desktop app and browser tab. Phones that already installed the app may keep the old icon until it's reopened — some need it removed and added again.";
  }
  return (
    <div className="xl:sticky xl:top-4 space-y-2.5">
      <div className="inline-flex gap-0.5 p-[3px] rounded-[10px] border border-line bg-card flex-wrap" role="group" aria-label="Preview">
        {[
          ["desk", "Sign-in · desktop"],
          ["phone", "Sign-in · phone"],
          ["icon", "Icon & tab"],
        ].map(([k, l]) => (
          <button key={k} type="button" aria-pressed={tab === k} onClick={() => setTab(k)} className={cn("h-[30px] px-3 rounded-lg text-[12.5px] font-semibold", tab === k ? "bg-brand-light text-ink" : "text-muted2")}>
            {l}
          </button>
        ))}
      </div>
      <div className="rounded-2xl border border-line bg-[#E9E6DF] dark:bg-[#121015] p-4 flex justify-center">{body}</div>
      <p className="text-[12px] text-muted2">{note}</p>
    </div>
  );
}

/** Platform console → Settings → Appearance. */
export function PlatformSettings() {
  const [saved, setSaved] = useState(null);
  const [f, setF] = useState(null);
  const [icon, setIcon] = useState({ data: null, remove: false });
  const [promo, setPromo] = useState({ data: null, remove: false });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api
      .get("/platform")
      .then(({ data }) => {
        setSaved(data);
        setF({ ...DEFAULTS, ...data });
        setIcon({ data: null, remove: false });
        setPromo({ data: null, remove: false });
      })
      .catch((e) => toast.error(apiError(e)));
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  if (!f)
    return (
      <Layout owner title="Platform settings" subtitle="Applies to the whole app, not one workspace">
        <LoadingPanel rows={4} />
      </Layout>
    );

  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? (v.target.type === "checkbox" ? v.target.checked : v.target.value) : v }));
  const iconSrc = icon.remove ? null : icon.data || (saved.icon_v ? saved.icon_url : null);
  const promoSrc = promo.remove ? null : promo.data || saved.promo_url || null;
  const dirty =
    icon.data ||
    icon.remove ||
    promo.data ||
    promo.remove ||
    Object.keys(DEFAULTS).some((k) => (f[k] ?? "") !== (saved[k] ?? DEFAULTS[k] ?? ""));

  const save = async (e) => {
    e?.preventDefault();
    if (!f.platform_name.trim()) return toast.error("Give the app a name");
    if (f.cta_on && f.cta_url && !/^https?:\/\//i.test(f.cta_url)) return toast.error("The button link should start with https://");
    if (f.login_mode !== "standard" && !promoSrc) return toast.error("Upload a promo image, or choose Standard");
    setBusy(true);
    try {
      if (icon.data) await api.post("/platform/assets/icon", { data: icon.data });
      else if (icon.remove) await api.delete("/platform/assets/icon");
      if (promo.data) await api.post("/platform/assets/promo", { data: promo.data });
      else if (promo.remove) await api.delete("/platform/assets/promo");
      const body = Object.fromEntries(Object.keys(DEFAULTS).map((k) => [k, f[k]]));
      await api.put("/platform", body);
      try {
        window.localStorage.setItem("platform_name", f.platform_name);
      } catch {
        /* private browsing */
      }
      await loadMeta(true);
      toast.success("Saved — everyone sees the new look now");
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout
      owner
      title="Platform settings"
      subtitle="Applies to the whole app, not one workspace"
      action={
        <Button onClick={save} disabled={busy || !dirty} className="bg-brand hover:bg-brand-hover">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
        </Button>
      }
    >
      <div className="max-w-[1200px] mb-4">
        <h2 className="font-display text-[20px] font-semibold">Appearance</h2>
        <p className="text-[13px] text-muted2">How the app looks before and after sign-in, for everyone: its name, icon, colour and the sign-in page.</p>
      </div>
      <div className="grid xl:grid-cols-[minmax(0,440px)_minmax(0,1fr)] gap-5 items-start max-w-[1200px]">
        <form onSubmit={save} className="panel overflow-hidden" style={brandVars(f.brand_color)}>
          <Section title="App name & icon">
            <label className="block">
              <span className="block text-[12px] font-semibold mb-1.5">App name</span>
              <input className={inputCls} required maxLength={30} value={f.platform_name} onChange={set("platform_name")} />
              <span className="block text-[11.5px] text-muted2 mt-1">On the sign-in page, in the browser tab and under the icon on phones.</span>
            </label>
            <ImageUpload
              label="App icon"
              hint="Square image, at least 512 × 512. PNG works best. For the installed app, the browser tab and the sign-in page."
              thumb={<IconPreview src={iconSrc} color={f.brand_color} size={56} radius={0} />}
              onPick={(data) => setIcon({ data, remove: false })}
              busy={busy}
            />
            {iconSrc && (
              <button type="button" className="text-[12.5px] font-semibold text-muted2 underline underline-offset-2" onClick={() => setIcon({ data: null, remove: !!saved.icon_v })}>
                Use the standard {f.platform_name || "MetaTrack"} icon
              </button>
            )}
          </Section>

          <Section title="Colour">
            <div className="flex gap-2 flex-wrap items-center" role="group" aria-label="Colour">
              {COLORS.map(([c, n]) => (
                <button
                  key={c}
                  type="button"
                  title={n}
                  aria-label={n}
                  aria-pressed={f.brand_color.toLowerCase() === c.toLowerCase()}
                  onClick={() => set("brand_color")(c)}
                  className={cn("w-[30px] h-[30px] rounded-full border-2 border-card", f.brand_color.toLowerCase() === c.toLowerCase() ? "ring-2 ring-ink" : "ring-1 ring-line")}
                  style={{ background: c }}
                />
              ))}
              <label className="inline-flex items-center gap-2 h-8 px-2.5 rounded-lg border border-line text-[12.5px] font-semibold cursor-pointer">
                Custom
                <input type="color" value={f.brand_color} onChange={set("brand_color")} className="w-6 h-5 p-0 border-0 bg-transparent cursor-pointer" aria-label="Custom colour" />
              </label>
            </div>
            <p className="text-[11.5px] text-muted2">For the sign-in page, the standard icon and the installed app's top bar. Each workspace keeps its own colour inside the app (its Settings).</p>
          </Section>

          <Section title="Sign-in page">
            <div className="inline-flex gap-0.5 p-[3px] rounded-[10px] border border-line bg-surface flex-wrap" role="group" aria-label="Sign-in style">
              {MODES.map(([k, l]) => (
                <button key={k} type="button" aria-pressed={f.login_mode === k} onClick={() => set("login_mode")(k)} className={cn("h-[30px] px-3 rounded-lg text-[12.5px] font-semibold", f.login_mode === k ? "bg-card text-ink shadow-sm" : "text-muted2")}>
                  {l}
                </button>
              ))}
            </div>
            <p className="text-[11.5px] text-muted2 -mt-1">{MODES.find((m) => m[0] === f.login_mode)?.[2]}</p>
            {f.login_mode !== "standard" && (
              <>
                <ImageUpload
                  label="Promo image"
                  hint="Landscape, at least 1600 × 1200. JPG or PNG. Crops to fit, centred."
                  wide
                  thumb={promoSrc ? <img src={promoSrc} alt="" className="w-full h-full object-cover" /> : null}
                  onPick={(data) => setPromo({ data, remove: false })}
                  busy={busy}
                />
                {promoSrc && (
                  <button type="button" className="text-[12.5px] font-semibold text-muted2 underline underline-offset-2" onClick={() => setPromo({ data: null, remove: !!saved.promo_v })}>
                    Remove the promo image
                  </button>
                )}
              </>
            )}
            {f.login_mode !== "photo" && (
              <>
                <label className="block">
                  <span className="block text-[12px] font-semibold mb-1.5">Headline</span>
                  <input className={inputCls} maxLength={90} value={f.tagline} onChange={set("tagline")} />
                </label>
                <label className="block">
                  <span className="block text-[12px] font-semibold mb-1.5">Text</span>
                  <textarea className={cn(inputCls, "h-20 py-2")} maxLength={240} value={f.blurb} onChange={set("blurb")} />
                </label>
              </>
            )}
            <label className="flex items-center gap-2.5 text-[13px] font-semibold cursor-pointer">
              <input type="checkbox" className="w-4 h-4 accent-brand" checked={!!f.cta_on} onChange={set("cta_on")} /> Add a button
            </label>
            {f.cta_on && (
              <div className="grid grid-cols-2 gap-2.5">
                <label className="block">
                  <span className="block text-[12px] font-semibold mb-1.5">Button text</span>
                  <input className={inputCls} maxLength={24} value={f.cta_text} onChange={set("cta_text")} placeholder="Listen now" />
                </label>
                <label className="block">
                  <span className="block text-[12px] font-semibold mb-1.5">Opens</span>
                  <input className={inputCls} type="url" value={f.cta_url} onChange={set("cta_url")} placeholder="https://" />
                </label>
              </div>
            )}
            {f.login_mode !== "standard" && (
              <label className="block">
                <span className="block text-[12px] font-semibold mb-1.5">Show the promo until</span>
                <input className={inputCls} type="date" value={f.promo_until || ""} onChange={set("promo_until")} />
                <span className="block text-[11.5px] text-muted2 mt-1">Leave empty to keep it up. After this date the sign-in page goes back to Standard by itself.</span>
              </label>
            )}
          </Section>
          <div className="px-5 py-3.5 border-t border-line flex gap-2 items-center flex-wrap">
            <span className="text-[11.5px] text-muted2 flex-1 min-w-[160px]">{dirty ? "Unsaved changes." : "Changes show for everyone as soon as you save."}</span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setF({ ...DEFAULTS });
                setIcon({ data: null, remove: !!saved.icon_v });
                setPromo({ data: null, remove: !!saved.promo_v });
              }}
            >
              Reset to defaults
            </Button>
            <Button type="submit" size="sm" disabled={busy || !dirty} className="bg-brand hover:bg-brand-hover">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
            </Button>
          </div>
        </form>
        <Preview f={f} icon={iconSrc} promo={promoSrc} />
      </div>
    </Layout>
  );
}
